import React, { useCallback, useState } from 'react';
import { CornerDownLeft } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import OnboardingPill from './OnboardingPill';

interface OnboardingStepSelectProps {
  question: string;
  subtitle: string;
  options: string[];
  type: 'select' | 'multi-select';
  maxSelections?: number;
  onAnswer: (value: string | string[]) => void;
}

const OnboardingStepSelect: React.FC<OnboardingStepSelectProps> = ({
  question,
  subtitle,
  options,
  type,
  maxSelections,
  onAnswer,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const { t } = useTranslation();

  const handleClick = useCallback(
    (option: string) => {
      if (type === 'select') {
        onAnswer(option);
        return;
      }
      setSelected((prev) => {
        if (prev.includes(option)) return prev.filter((o) => o !== option);
        if (maxSelections && prev.length >= maxSelections) return prev;
        return [...prev, option];
      });
    },
    [type, maxSelections, onAnswer],
  );

  const handleContinue = () => {
    if (selected.length > 0) {
      onAnswer(selected);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && selected.length > 0) {
      onAnswer(selected);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      className="outline-none">
      <p className="text-white/40 text-sm mb-2">{subtitle}</p>
      <h2 className="text-2xl md:text-3xl font-semibold text-white mb-8">{question}</h2>
      <div className="flex flex-wrap gap-3">
        {options.map((option, index) => (
          <OnboardingPill
            key={option}
            label={option}
            index={index}
            selected={selected.includes(option)}
            onClick={() => handleClick(option)}
          />
        ))}
      </div>
      <AnimatePresence initial={false}>
        {type === 'multi-select' && selected.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="overflow-hidden">
            <button
              type="button"
              onClick={handleContinue}
              className="mt-6 px-6 py-2.5 rounded-full bg-[#7c6cf6] text-white text-sm font-medium hover:bg-[#6b5ce5] transition-colors cursor-pointer focus:outline-none">
              {t('Agent.OnboardingContinueWith', { count: selected.length, plural: selected.length > 1 ? 's' : '' })}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
      {type === 'multi-select' ? (
        <p className="text-white/20 text-xs mt-4 flex items-center gap-1.5">
          {t('Agent.OnboardingPressEnter')} <CornerDownLeft className="w-3 h-3" />
        </p>
      ) : null}
    </motion.div>
  );
};

export default OnboardingStepSelect;
